import React, { useState, useEffect } from 'react';
import { batchApi } from '../../../api/batchApi';
import { BatchItemExecutionHistory, STATUS_ICONS, STATUS_NAMES, STATUS_COLORS, BatchExecutionStatus } from '../../../types/batch';
import { Button } from '../../ui/button';

interface Props {
  historyId: number;
}

/**
 * 배치 실행 이력 상세 모달에 붙는 "대상 계정별 처리 내역" 목록.
 * before/after 상태를 원문 JSON으로 펼쳐볼 수 있어, "화면엔 반영됐는데 특정 계정만
 * 스킵/실패했는지"를 배치 이력만으로 추적할 수 있게 한다.
 */
export const BatchExecutionItemsList: React.FC<Props> = ({ historyId }) => {
  const [items, setItems] = useState<BatchItemExecutionHistory[]>([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyId, page]);

  const fetchItems = async () => {
    try {
      setIsLoading(true);
      const response = await batchApi.getBatchHistoryItems(historyId, page, 20);
      if (response.data.success) {
        setItems(response.data.data);
        setTotalPages(response.data.totalPages || 0);
        setTotalElements(response.data.totalElements || 0);
      }
    } catch (error) {
      console.error('배치 개별 건 이력 조회 실패:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadgeClass = (status: BatchExecutionStatus) => {
    const color = STATUS_COLORS[status] || 'gray';
    const colorMap: Record<string, string> = {
      green: 'bg-green-100 text-green-800',
      red: 'bg-red-100 text-red-800',
      blue: 'bg-blue-100 text-blue-800',
      orange: 'bg-orange-100 text-orange-800',
      gray: 'bg-gray-100 text-gray-800'
    };
    return colorMap[color];
  };

  const formatJson = (raw: string | null): string => {
    if (!raw) return '-';
    try {
      return JSON.stringify(JSON.parse(raw), null, 2);
    } catch {
      return raw;
    }
  };

  return (
    <div className="border-t pt-4 mt-4">
      <div className="text-sm text-gray-500 mb-2">
        대상 계정별 처리 내역 {totalElements > 0 && `(총 ${totalElements}건)`}
      </div>

      {isLoading ? (
        <div className="text-center py-6 text-gray-500 text-sm">로딩 중...</div>
      ) : items.length === 0 ? (
        <div className="text-center py-6 text-gray-500 text-sm">
          이 배치 실행에 대한 건별 이력이 없습니다.
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border p-2 text-left font-medium">대상</th>
                  <th className="border p-2 text-left font-medium">상태</th>
                  <th className="border p-2 text-left font-medium">실행 시각</th>
                  <th className="border p-2 text-left font-medium">변경 필드</th>
                  <th className="border p-2 text-left font-medium">에러</th>
                  <th className="border p-2 text-center font-medium">상세</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <React.Fragment key={item.id}>
                    <tr className="hover:bg-gray-50">
                      <td className="border p-2">
                        {item.entityType || '-'}: {item.entityId || '-'}
                      </td>
                      <td className="border p-2">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusBadgeClass(item.status)}`}>
                          {STATUS_ICONS[item.status]} {STATUS_NAMES[item.status]}
                        </span>
                      </td>
                      <td className="border p-2 whitespace-nowrap">
                        {new Date(item.executionTime).toLocaleString('ko-KR')}
                      </td>
                      <td className="border p-2">
                        {(() => {
                          try {
                            const fields: string[] = item.changedFields ? JSON.parse(item.changedFields) : [];
                            return fields.length > 0 ? fields.join(', ') : '-';
                          } catch {
                            return '-';
                          }
                        })()}
                      </td>
                      <td className="border p-2 text-red-700">
                        {item.errorType ? `${item.errorType}${item.errorMessage ? `: ${item.errorMessage}` : ''}` : '-'}
                      </td>
                      <td className="border p-2 text-center">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setExpandedId(expandedId === item.id ? null : item.id)}
                        >
                          {expandedId === item.id ? '접기' : 'before/after'}
                        </Button>
                      </td>
                    </tr>
                    {expandedId === item.id && (
                      <tr>
                        <td colSpan={6} className="border p-3 bg-gray-50">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <div className="text-xs font-medium text-gray-600 mb-1">처리 전 (beforeState)</div>
                              <pre className="text-xs bg-white border rounded p-2 overflow-x-auto max-h-48">
                                {formatJson(item.beforeState)}
                              </pre>
                            </div>
                            <div>
                              <div className="text-xs font-medium text-gray-600 mb-1">처리 후 (afterState)</div>
                              <pre className="text-xs bg-white border rounded p-2 overflow-x-auto max-h-48">
                                {formatJson(item.afterState)}
                              </pre>
                            </div>
                          </div>
                          {item.errorDetails && (
                            <div className="mt-2">
                              <div className="text-xs font-medium text-gray-600 mb-1">에러 상세</div>
                              <pre className="text-xs bg-white border rounded p-2 overflow-x-auto max-h-48 text-red-800">
                                {item.errorDetails}
                              </pre>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="mt-3 flex justify-center items-center gap-3">
              <Button size="sm" variant="outline" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>
                이전
              </Button>
              <span className="text-xs text-gray-600">{page + 1} / {totalPages}</span>
              <Button size="sm" variant="outline" onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}>
                다음
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};
